<template>
  <div class="app-box">
    <HomeNavigationBar :nav-data="pageData.navs" />

    <!-- Hero Section -->
    <section class="relative pt-32 pb-20 bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white overflow-hidden">
      <!-- Decorative background glow -->
      <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px] pointer-events-none"></div>
      <div class="absolute -bottom-20 left-10 w-[300px] h-[300px] bg-indigo-300/15 rounded-full blur-[80px] pointer-events-none"></div>
      
      <div class="max-w-7xl mx-auto px-6 relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <!-- Left Content -->
          <div class="lg:col-span-6 text-left flex flex-col justify-center">
            <span class="text-xs px-3 py-1.5 bg-blue-100/60 border border-blue-200 text-blue-700 rounded-full font-semibold tracking-wider mb-6 inline-block w-fit">
              TDuckPro 经典稳定版
            </span>
            <h1 class="text-4xl lg:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
              TDuckPro<br />
              <span class="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                填鸭表单问卷系统
              </span>
            </h1>
            <h2 class="text-xl font-medium text-slate-700 mb-4">{{ pageData.contentOne.titleList[1] }}</h2>
            <p class="text-slate-600 mb-8 leading-relaxed text-base">
              {{ pageData.contentOne.content }}
            </p>
            
            <!-- Links -->
            <div class="flex flex-wrap gap-x-6 gap-y-2 mb-8">
              <a v-for="link in pageData.contentOne.linkTextList" :key="link.label" :href="link.link" target="_blank"
                class="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1">
                {{ link.label }}
              </a>
            </div>
            
            <!-- Actions -->
            <div class="flex flex-wrap gap-4">
              <a :href="pageData.contentOne.buttonList[0].link" target="_blank"
                class="px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all duration-300 shadow-md hover:shadow-lg inline-block">
                {{ pageData.contentOne.buttonList[0].label }}
              </a>
              <a :href="pageData.contentOne.buttonList[1].link" target="_blank"
                class="px-8 py-3.5 border border-slate-300 hover:border-slate-400 text-slate-700 rounded-xl font-bold transition-all duration-300 hover:bg-slate-50 inline-block">
                {{ pageData.contentOne.buttonList[1].label }}
              </a>
            </div>
          </div>
          
          <!-- Right Content (Carousel Showcase) -->
          <div class="lg:col-span-6 w-full">
            <div class="p-3 rounded-[32px] bg-white border border-slate-200/80 shadow-[0_20px_50px_rgba(59,130,246,0.08)]">
              <div class="rounded-2xl overflow-hidden bg-slate-50 border border-slate-100">
                <n-carousel autoplay dot-type="line" :show-dots="true" class="w-full h-auto">
                  <img v-for="item in pageData.contentOne.imageUrls" :key="item.url"
                    class="w-full h-auto object-cover opacity-90 transition-opacity hover:opacity-100" 
                    :src="item.url" alt="界面演示">
                </n-carousel>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Mobile Plugin Section -->
    <section class="py-20 bg-slate-50 overflow-hidden border-y border-slate-100">
      <div class="max-w-7xl mx-auto px-6">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <!-- Left Side: Phone & QR Code -->
          <div class="lg:col-span-5 flex flex-col md:flex-row items-center justify-center gap-8">
            <div class="p-6 bg-white rounded-3xl border border-slate-200/80 shadow-md flex flex-col items-center max-w-[280px]">
              <img :src="pageData.featureOne.images" alt="QR Code" class="w-48 h-48 object-contain mb-4" />
              <span class="text-xs text-slate-500 font-medium text-center">扫码体验手机端填报</span>
            </div>
            <!-- A subtle mobile design aspect -->
            <div class="hidden md:flex flex-col items-start gap-3 text-left">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                <span class="text-xs text-slate-600 font-bold">支持微信小程序</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                <span class="text-xs text-slate-600 font-bold">支持 Android/iOS APP</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-indigo-500"></span>
                <span class="text-xs text-slate-600 font-bold">支持 H5 填报与核销</span>
              </div>
            </div>
          </div>
          
          <!-- Right Side: Content -->
          <div class="lg:col-span-7 text-left flex flex-col justify-center">
            <span class="text-xs px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-full font-semibold tracking-wider mb-6 inline-block w-fit">
              MOBILE PLUGIN
            </span>
            <h3 class="text-2xl lg:text-3xl font-extrabold text-slate-900 mb-6 leading-snug whitespace-pre-line">
              {{ pageData.featureOne.title }}
            </h3>
            <p class="text-slate-600 mb-8 leading-relaxed text-base">
              {{ pageData.featureOne.description }}
            </p>
            <a :href="pageData.featureOne.linkText.link" target="_blank"
              class="inline-flex items-center text-blue-600 hover:text-blue-800 font-bold transition-all group">
              {{ pageData.featureOne.linkText.label }}
              <i class="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Advanced Features Grid -->
    <section class="py-24 bg-white">
      <div class="max-w-7xl mx-auto px-6">
        <div class="text-center mb-16 max-w-3xl mx-auto">
          <span class="text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full font-semibold tracking-wider mb-4 inline-block">
            ADVANCED EXTENSIONS
          </span>
          <h2 class="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
            {{ pageData.featureTwo.title }}
          </h2>
          <p class="text-slate-600 text-base leading-relaxed">
            {{ pageData.featureTwo.description }}
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="item in pageData.featureTwo.extendList" :key="item.description"
            class="group p-6 rounded-2xl border border-slate-100 bg-white hover:bg-slate-50/50 hover:shadow-md hover:border-slate-200 transition-all duration-300 flex flex-col text-left"
          >
            <div class="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6 transition-colors group-hover:bg-blue-100">
              <img :src="item.images" alt="Feature Icon" class="w-6 h-6 object-contain" />
            </div>
            <h4 class="text-lg font-bold text-slate-900 mb-3 flex items-center gap-1.5">
              <a v-if="item.linkTextList && item.linkTextList[0].link" :href="item.linkTextList[0].link" target="_blank"
                class="hover:text-blue-600 transition-colors">
                {{ item.linkTextList[0].label }}
              </a>
              <span v-else>{{ item.linkTextList ? item.linkTextList[0].label : '' }}</span>
            </h4>
            <p class="text-sm text-slate-500 leading-relaxed flex-grow">
              {{ item.description }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <HomePricingOne id="pricing-section" :pricing-data="pageData.pricingOne" />

    <!-- Security and Delivery -->
    <section class="py-20 bg-slate-50 border-t border-slate-100">
      <div class="max-w-7xl mx-auto px-6">
        <div class="text-center mb-16 max-w-3xl mx-auto">
          <h2 class="text-3xl font-extrabold text-slate-900 mb-4">{{ pageData.featureThree.title }}</h2>
          <p class="text-slate-500 uppercase tracking-widest text-xs font-bold">{{ pageData.featureThree.description }}</p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div v-for="item in pageData.featureThree.serveList" :key="item.title"
            class="bg-white p-8 rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow flex flex-col text-left">
            <div class="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-6">
              <img :src="item.images" alt="Service Icon" class="w-6 h-6 object-contain" />
            </div>
            <h4 class="text-lg font-bold text-slate-900 mb-3">{{ item.title }}</h4>
            <p class="text-sm text-slate-600 leading-relaxed">{{ item.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Three Steps -->
    <section class="py-24 bg-white">
      <div class="max-w-7xl mx-auto px-6">
        <div class="text-center mb-16 max-w-3xl mx-auto">
          <span class="text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full font-semibold tracking-wider mb-4 inline-block">
            THREE STEPS WORKFLOW
          </span>
          <h2 class="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
            {{ pageData.featureFour.title }}
          </h2>
          <p class="text-slate-600 text-base leading-relaxed">
            {{ pageData.featureFour.description }}
          </p>
        </div>
        
        <div class="space-y-20">
          <div v-for="(project, index) in pageData.featureFour.projectList" :key="project.header"
            :class="[
              'flex flex-col lg:flex-row justify-between items-center gap-12 lg:gap-16',
              index % 2 === 1 ? 'lg:flex-row-reverse' : ''
            ]"
          >
            <!-- Content -->
            <div class="w-full lg:w-[46%] text-left">
              <span class="text-2xl font-bold text-blue-600 block mb-6 border-b pb-2 border-slate-100">{{ project.header }}</span>
              <div class="space-y-6">
                <div v-for="tab in project.tabList" :key="tab.title" class="flex items-start gap-4">
                  <div class="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0 mt-1">
                    <i class="fas fa-check text-[10px]"></i>
                  </div>
                  <div>
                    <h4 class="font-bold text-slate-800 text-base">{{ tab.title }}</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">{{ tab.description }}</p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Image Showcase -->
            <div class="w-full lg:w-[46%] p-3 bg-slate-50 border border-slate-100 rounded-2xl shadow-inner">
              <img :src="project.images" :alt="project.header" class="w-full h-auto rounded-lg shadow-sm" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Purchase Flow -->
    <section class="py-20 bg-slate-50 border-y border-slate-100">
      <div class="max-w-7xl mx-auto px-6">
        <div class="text-center mb-16 max-w-3xl mx-auto">
          <span class="text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full font-semibold tracking-wider mb-4 inline-block">
            TIMELINE STEPS
          </span>
          <h2 class="text-3xl font-extrabold text-slate-900 mb-4">{{ pageData.featureFive.title }}</h2>
          <p class="text-slate-600 text-base leading-relaxed">{{ pageData.featureFive.description }}</p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div v-for="(step, index) in pageData.featureFive.buyList" :key="step.title"
            class="bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm relative text-left"
          >
            <!-- Number Badge -->
            <div class="absolute -top-4 -left-4 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-md">
              {{ index + 1 }}
            </div>
            <div class="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
              <img :src="step.images" alt="Step Icon" class="w-6 h-6 object-contain" />
            </div>
            <h4 class="text-base font-bold text-slate-900 mb-2">{{ step.title }}</h4>
            <p class="text-xs text-slate-500 leading-relaxed">{{ step.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 先进组织的共同选择 (Logo 墙) -->
    <section class="py-24 bg-white">
      <div class="max-w-7xl mx-auto px-6 relative z-10">
        <div class="text-center mb-16 max-w-3xl mx-auto">
          <span class="text-xs px-3 py-1.5 bg-slate-100 border border-slate-200 text-slate-600 rounded-full font-semibold tracking-wider mb-4 inline-block">
            CLIENT STORIES
          </span>
          <h2 class="text-4xl font-extrabold text-slate-900 mb-6 leading-tight">先进组织的共同选择</h2>
          <p class="text-lg text-slate-600 leading-relaxed">
            每个行业，都有我们倾力保障并成功落地的数字化案例。
          </p>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-16">
          <div v-for="(logo, index) in logoWall" :key="index"
            class="h-16 rounded-xl bg-white border border-slate-100 flex items-center justify-center p-3 hover:shadow-md hover:border-slate-200 transition-all duration-300">
            <img :src="logo" alt="company logo"
              class="h-10 w-auto object-contain transition-all duration-300 hover:scale-105" />
          </div>
        </div>

        <div class="text-center mb-10">
          <NuxtLink to="/case/CaseList"
            class="inline-flex items-center justify-center px-8 py-3.5 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-full font-bold transition-all duration-300 shadow-sm hover:shadow-[0_10px_20px_-8px_rgba(59,130,246,0.4)] cursor-pointer group">
            查看更多客户案例 <i class="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform" />
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- 通用底部 -->
    <TheFooter />
  </div>
</template>

<script setup lang="ts">
import HomeNavigationBar from '../components/home/NavigationBar.vue'
import HomePricingOne from '../components/home/PricingOne.vue'
import TheFooter from '~/components/home/TheFooter.vue'

useHead({
  title: 'TDuckPro填鸭表单问卷系统 — Vue2经典架构 | Tduck填鸭表单',
  meta: [
    {
      name: 'description',
      content: 'TDuckPro填鸭表单问卷系统是面向企业私有化部署的成熟稳定问卷调查系统和表单系统，采用Vue2技术栈，支持可视化表单设计、数据收集、统计分析、流程协作与系统集成。'
    }
  ],
  link: [
    { rel: 'icon', href: '/pro.ico', sizes: 'any' },
    { key: 'canonical', rel: 'canonical', href: 'https://www.tduckcloud.com/pro' },
    { rel: 'stylesheet', href: '//at.alicdn.com/t/c/font_4348154_m5w0r2ywl.css' }
  ]
})

useSeoMeta({
  title: 'TDuckPro填鸭表单问卷系统 — Vue2经典架构 | Tduck填鸭表单',
  ogTitle: 'TDuckPro填鸭表单问卷系统 — Vue2经典架构',
  keywords: 'TDuckPro,经典版,表单问卷系统,Vue2,私有化表单系统,问卷生成工具,企业问卷系统源码,私有化部署表单系统,满意度调查系统',
  description: 'TDuckPro填鸭表单问卷系统是面向企业私有化部署的成熟稳定问卷调查系统和表单系统，采用Vue2技术栈，支持表单设计、数据收集、协作流转。适用于已有项目升级与历史系统维护。',
  ogDescription: 'TDuckPro经典版采用Vue2经典技术架构，提供企业级数据采集和协作能力，保障系统稳定与数据安全。'
})

const logoWall = [
  'https://minio.tduckcloud.com/landing/icons/zhongkeyuan.png',
  'https://minio.tduckcloud.com/landing/icons/tengxunyinyu.png',
  'https://minio.tduckcloud.com/landing/icons/xianjiaoda.png',
  'https://minio.tduckcloud.com/landing/icons/wuhandaxue.png',
  'https://minio.tduckcloud.com/landing/icons/qinghua.png',
  'https://minio.tduckcloud.com/landing/icons/guangzhoudaxue.png',
  'https://minio.tduckcloud.com/landing/icons/hdsfdx.png',
  'https://minio.tduckcloud.com/landing/icons/bjjd.png',
  'https://minio.tduckcloud.com/landing/icons/scdx.png',
  'https://minio.tduckcloud.com/landing/icons/bosch.png',
  'https://minio.tduckcloud.com/landing/icons/foxconn.png',
  'https://minio.tduckcloud.com/landing/icons/jmj.png',
  'https://minio.tduckcloud.com/landing/icons/shiyou.png',
  'https://minio.tduckcloud.com/landing/icons/yidong.png',
  'https://minio.tduckcloud.com/landing/icons/liantong.png',
  'https://minio.tduckcloud.com/landing/icons/dianxin.png',
  'https://minio.tduckcloud.com/landing/icons/qiaqia.png',
  'https://minio.tduckcloud.com/landing/icons/gjyl.png',
  'https://minio.tduckcloud.com/landing/icons/dji.png',
  'https://minio.tduckcloud.com/landing/icons/geely.png',
  'https://minio.tduckcloud.com/landing/icons/lingxi.png',
  'https://minio.tduckcloud.com/landing/icons/Hisense1.png',
  'https://minio.tduckcloud.com/landing/icons/xinshidai.png',
  'https://minio.tduckcloud.com/landing/icons/sfc.png',
  'https://minio.tduckcloud.com/landing/icons/itg.png',
  'https://minio.tduckcloud.com/landing/icons/haier.png',
  'https://minio.tduckcloud.com/landing/icons/991.png',
  'https://minio.tduckcloud.com/landing/icons/sichuanjichang.png',
  'https://minio.tduckcloud.com/landing/icons/zgrs@1x.png',
  'https://minio.tduckcloud.com/landing/icons/cryl.png',
  'https://minio.tduckcloud.com/landing/icons/yfm.png',
  'https://minio.tduckcloud.com/landing/icons/zhjt.png',
  'https://minio.tduckcloud.com/landing/icons/zgbq.png',
  'https://minio.tduckcloud.com/landing/icons/saj.png',
  'https://minio.tduckcloud.com/landing/icons/sdgs.png',
  'https://minio.tduckcloud.com/landing/icons/hdrm.png',
  'https://minio.tduckcloud.com/landing/icons/gjdszj.png',
  'https://minio.tduckcloud.com/landing/icons/tyszf.png',
  'https://minio.tduckcloud.com/landing/icons/xmszf.png',
  'https://minio.tduckcloud.com/landing/icons/Nikon.png',
  'https://minio.tduckcloud.com/landing/icons/dzqc.png',
  'https://minio.tduckcloud.com/landing/icons/Porsche.png'
]

const pageData = {
  navs: {
    logoImg: 'https://minio.tduckcloud.com/landing/bb326f9678cb4874b0a5e53b186ded0d.png',
    // logoImg: '    https://minio.tduckcloud.com/landing/logo-red.png',
    logoLink: '/',
    menus: [
      {
        label: 'TDuckX',
        link: '/x',
        blank: false,
        new: true
      },
      {
        label: '应用场景',
        link: '/solutions',
        blank: false
      },
      {
        label: '价格',
        linkType: 2,
        blank: false // false 不跳转
      },
      {
        label: '文档',
        link: 'https://doc.tduckcloud.com/'
      },
      {
        label: '产品列表',
        // link: 'https://gitee.com/TDuckApp/tduck-platform',
        options: [
          {
            label: 'TDuck - 社区版',
            key: 'marina bay sands',
            props: {
              onClick: () => {
                window.open('https://gitee.com/TDuckApp/tduck-platform')
              }
            }
            // disabled: true
          },
          {
            label: '经典版',
            props: {
              onClick: () => {
                window.open('/pro')
              }
            }
          },
          {
            label: 'TDuckX-Vue3版',
            props: {
              onClick: () => {
                window.open('/x')
              }
            }
          },
          {
            label: 'TReport - 数据大屏',
            props: {
              onClick: () => {
                window.open('https://gitee.com/TDuckApp/tduck-report-platform')
              }
            }
          }
        ]
      },

      {
        label: '咨询',
        link: 'https://doc.tduckcloud.com/buyPro/',
        blank: true // false 不跳转
      },
      {
        label: 'Star支持项目',
        link: 'https://gitee.com/TDuckApp/tduck-platform',
        blank: true // false 不跳转
      }
    ]
  },
  contentOne: {
    titleList: [
      'TDuckPro填鸭表单问卷系统',
      '成熟稳定 · Vue2经典架构'
    ],
    content: '本版本采用 Vue2 经典架构，成熟稳定，适用于已有项目升级与历史系统维护。新项目推荐首次体验企业版（Vue3新架构）。',
    linkTextList: [
      {
        label: '典型用户 >',
        link: 'https://doc.tduckcloud.com/buyPro/customer.html'
      },
      {
        label: '功能对比清单 >',
        link: 'https://docs.qq.com/sheet/DSUhoR2pOc2RuZ0Va?tab=BB08J2'
      }
    ],
    buttonList: [
      {
        label: '体验经典版',
        link: 'https://pro.tduckcloud.com/',
        buttonColor: '#228aff'
      },
      {
        label: '点击查看介绍 >',
        link: 'https://doc.tduckcloud.com/functionDesc/',
        buttonColor: '#228aff'
      }
    ],
    imageUrls: [
      {
        url: 'https://minio.tduckcloud.com/landing/images/biaodan.png'
      },
      {
        url: 'https://minio.tduckcloud.com/landing/images/datahelper.png'
      },
      {
        url: 'https://minio.tduckcloud.com/landing/images/hexiao.png'
      }
    ]
  },
  carousel: {
  },
  featureOne: {
    title: '流程审批/预约核销/表单待填/管理表单\n快使用Uniapp插件，手机操作更方便！',
    description: '使用uniapp框架，支持多端打包多端小程序，同时支持Android、iOS 打包成APP；实现多终端管理/填报表单数据（测试账号同PC端）',
    linkText: {
      label: '查看详情',
      link: 'https://doc.tduckcloud.com/functionDesc/uniappDesc.html'
    },
    images: 'https://minio.tduckcloud.com/landing/images/uniappQrCode.png'
  },
  featureTwo: {
    title: '更多高级扩展功能，持续更新 ING',
    description: '开箱即用的TduckPRO新服务',
    extendList: [
      {
        linkTextList: [{
          label: '表单协同',
          link: 'https://doc.tduckcloud.com/example/collaborativeManagement.html'
        }
        ],
        images: 'https://minio.tduckcloud.com/landing/20230926155219.png',
        description: '可公开发布或仅对某一个用户组进行填写或分享给指定人员协作管理'
      },
      {
        linkTextList: [{
          label: '流程表单',
          link: 'https://doc.tduckcloud.com/example/flowableForm.html'
        }
        ],
        images: 'https://minio.tduckcloud.com/landing/20230926155239.png',
        description: '支持创建流程表单，根据设定的流程完成填报审批等执行流程'
      },
      {
        linkTextList: [{
          label: '自定义参数',
          link: 'https://doc.tduckcloud.com/proDeploy/othersConfigure.html'
        }
        ],
        images: 'https://minio.tduckcloud.com/landing/20230926155252.png',
        description: '后台填写平台名称、logo等信息，快速更改，无需复杂配置'
      },
      {
        linkTextList: [{
          label: '百万级数据收集能力'
        }
        ],
        images: 'https://minio.tduckcloud.com/landing/20230926155312.png',
        description: '重构表单数据存储逻辑，适用高并发场景，支持百万级数据收集'
      },
      {
        linkTextList: [{
          label: '40+组件',
          link: 'https://doc.tduckcloud.com/functionDesc/proDesc.html'
        }
        ],
        images: 'https://minio.tduckcloud.com/landing/20230926155331.png',
        description: '提供功能强大的高级组件，可适用在多种复杂的场景中'
      },
      {
        linkTextList: [{
          label: '图像识别'
        }
        ],
        images: 'https://minio.tduckcloud.com/landing/20230926155355.png',
        description: '基于第三方AI技术，提供高精度的文字识别，快速完成数据采集'
      },
      {
        linkTextList: [{
          label: '企业级UI'
        }
        ],
        images: 'https://minio.tduckcloud.com/landing/20230926155423.png',
        description: '使用全新的UI框架，视觉清新，自适应多端填报需求'
      },
      {
        linkTextList: [{
          label: '移动端支持'
        }
        ],
        images: 'https://minio.tduckcloud.com/landing/20230926155434.png',
        description: '移动端程序支持多端发布，统一后台，多填报渠道支持'
      }
    ]
  },
  pricingOne: {
    title: '为企业提供数据收集解决方案 | 限时特价',
    description: '选择一个您心仪的鸭鸭版本，立即开启信息收集新方式',
    linkText: {
      label: 'Pro价格计算器 >',
      link: 'https://pro.tduckcloud.com/s/dpYJqPM8'
    },
    firstCols: ['适用场景', '交付内容', '技术支持', '免费更新', '全量功能', '永久使用',
      'Uniapp插件', '使用范围'],
    colsList: [
      {
        header: {
          title: '私有化部署',
          price: ['<span style="font-size: 24px;color: #f56a00">¥1999</span><span style="font-size: 14px">/永久</span>'],
          buy: {
            bgColor: '#F56A00',
            textColor: '#fbfbfb',
            label: '立即购买',
            link: 'https://pro.tduckcloud.com/s/MTtFeWrH'
          }
        },
        dataList: [
          '适用于无需二开改造的数据收集或调研使用',
          '交付程序安装包',
          '3个月免费工单支持+更新',
          '已有功能永久BUG修复',
          '永久使用',
          '可增购Uniapp小程序',
          '1授权码 1机器'
        ]
      },
      {
        header: {
          title: '企业授权',
          price: ['<span style="font-size: 24px;color: #f56a00">¥3999</span><span style="font-size: 14px">/永久</span>'],
          buy: {
            bgColor: '#F56A00',
            textColor: '#fbfbfb',
            label: '立即购买',
            link: 'https://pro.tduckcloud.com/s/MTtFeWrH'
          }
        },
        dataList: [
          '适用于企业内部项目需二次开发集成时使用',
          '100%源码',
          '部署文档+使用手册',
          '3个月工单支持+更新',
          '永久使用',
          '可增购Uniapp小程序',
          '购买单位具有使用权'
        ]

      },
      {
        header: {
          title: '商用授权',
          price: ['<span style="font-size: 24px;color: #f56a00">¥????</span><span style="font-size: 14px">/永久</span>'],
          buy: {
            bgColor: '#000',
            textColor: '#ffdb8d',
            label: '获取价格',
            link: 'https://pro.tduckcloud.com/s/By3xkiBI'
          }
        },
        dataList: [
          '适用于表单收集能力集成衍生产品进行商业使用',
          '100%源码',
          '部署文档+使用手册+组件开发文档+接口文档',
          '6个月工单+对接群支持',
          '6个月免费更新',
          '永久使用',
          '包含Uniapp',
          '集成至衍生产品商业使用'
        ]
      },
      {
        header: {
          title: '订阅更新',
          price: ['<span style="font-size: 24px;color: #108ee9">¥699</span><span style="font-size: 14px">/半年期</span>'],
          buy: {
            bgColor: '#108ee9',
            textColor: '#fbfbfb',
            label: '立即购买',
            link: 'https://pro.tduckcloud.com/s/MTtFeWrH'
          }
        },
        dataList: [
          '适用于更新期限过期时增购更新服务',
          '所购套餐全新功能',
          '提供部署手册限时免费工单支持',
          '增购后有效期6个月',
          '所购套餐全量功能'
        ]

      }
    ]
  },
  featureThree: {
    title: '强大和安全，缺一不可',
    description: 'Strong and secure, indispensable',
    serveList: [
      {
        images: 'https://minio.tduckcloud.com/landing/20230926155758.png',
        title: '可100%源码交付',
        description: '开通git私服仓库账号，使用IDE工具可自行拉取项目代码，获得新的TDUCK\'PRO代码。'
      },
      {
        images: 'https://minio.tduckcloud.com/landing/20230926155807.png',
        title: '支持订阅更新',
        description: '首次购买后，能够支持订阅升级该购买程序的新功能升级，并享受后续其他新服务的优惠。'
      },
      {
        images: 'https://minio.tduckcloud.com/landing/20230926155827.png',
        title: '支持小程序/APP',
        description: '使用uniapp框架，支持打包多端小程序，同时支持Android/iOS 打包APP程序。'
      }
    ]
  },
  featureFour: {
    title: '三步，完成一个复杂的收集工程',
    description: '支持组件拖拽式生成表单，满足个性化管理需求',
    projectList: [{
      header: '1. 表单设计',
      tabList: [{
        title: '简单操作，快速创建表单',
        description: 'TDUCK提供40+定制组件。可以通过简单的拖拽，快速创建表单'
      },
      {
        title: 'DIY布局，多平台支持',
        description: '表单提供3种排版方式、支持设置个性化界面，多端打开使用表单，随时随地录入数据。'
      },
      {
        title: '强大功能，轻松高效',
        description: '自定义皮肤，设定属于品牌的独特风格；逻辑功能，题目逻辑自由灵活'
      }
      ],
      images: 'https://minio.tduckcloud.com/landing/images/onlineform.png'
    },
    {
      header: '2. 流程设定',
      tabList: [{
        title: '可视化界面，灵活设计业务流程',
        description: '通过简单的拖拉拽、连线操作，即可设计出一个实用的流程，将原线下流程搬到系统上。'
      },
      {
        title: '业务按照设定流程执行',
        description: '用户A发起流程处理，用户B接受消息并处理，用户B处理完成后流转至C....业务处理可视化。'
      },
      {
        title: 'PC/移动端，哪哪都行',
        description: '不管是手机、电脑，都可以接受消息、处理业务，员工不再为流程审批等问题“跑断腿”。'
      }
      ],
      images: 'https://minio.tduckcloud.com/landing/images/flowable.png'
    }, {
      header: '3.数据分析',
      tabList: [{
        title: '附件下载',
        description: '支持填报的附件打包下载，批量完成收集任务'
      },
      {
        title: '统计视图一目了然',
        description: '填报数量，表单链接点击数量，填报地域分析....一目了然'
      },
      {
        title: '支持题目分析',
        description: '精确到选项级别的收集数量/百分比分析'
      }
      ],
      images: 'https://minio.tduckcloud.com/landing/images/data.png'
    }
    ]

  },
  featureFive: {
    title: 'Pro版购买流程 | Purchase process',
    description: '流程简单清晰，快速响应需求；早购买早优惠！',
    buyList: [{
      images: 'https://minio.tduckcloud.com/landing/20230926155652.png',
      title: '微信咨询',
      description: '首先使用微信扫一扫添加商务人员进行沟通'
    }, {
      images: 'https://minio.tduckcloud.com/landing/20230926155625.png',
      title: '完成支付',
      description: '支持对公打款、微信支付、支付宝等付款渠道'
    },
    {
      images: 'https://minio.tduckcloud.com/landing/20230926155717.png',
      title: '提交授权信息',
      description: '提交链接后，发票7个工作日内将发送至邮箱'
    },
    {
      images: 'https://minio.tduckcloud.com/landing/20230926155738.png',
      title: '获取私服代码',
      description: '3分钟开通账户，使用账号至私服拉取'
    }
    ],
    images:
        'https://gw.alipayobjects.com/zos/basement_prod/167bee48-fbc0-436a-ba9e-c116b4044293.svg'
  },
  contenTwo: {
    title: 'ta们都在使用“填鸭表单”',
    linkTextList: {
      label: '查看典型用户 | Typical User',
      link: 'https://doc.tduckcloud.com/buyPro/customer.html'
    },
    imagesList: [
      { images: 'https://minio.tduckcloud.com/landing/icons/shiyou.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/yidong.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/liantong.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/dianxin.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/qiaqia.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/gjyl.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/dji.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/geely.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/lingxi.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/Hisense1.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/zhongkeyuan.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/tengxunyinyu.png' },
      // 教育用户
      { images: 'https://minio.tduckcloud.com/landing/icons/xianjiaoda.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/wuhandaxue.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/qinghua.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/guangzhoudaxue.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/hdsfdx.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/bjjd.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/scdx.png' },
      // 制造
      { images: 'https://minio.tduckcloud.com/landing/icons/bosch.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/foxconn.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/jmj.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/xinshidai.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/sfc.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/itg.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/haier.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/991.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/sichuanjichang.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/zgrs@1x.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/cryl.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/yfm.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/zhjt.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/zgbq.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/saj.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/sdgs.png' },
      // { images: 'https://minio.tduckcloud.com/landing/icons/tfws.png' },
      // { images: 'https://minio.tduckcloud.com/landing/icons/zjsh.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/hdrm.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/gjdszj.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/tyszf.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/xmszf.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/Nikon.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/dzqc.png' },
      { images: 'https://minio.tduckcloud.com/landing/icons/Porsche.png' }
    ]
  },
  footer: {
    logoUrl: 'https://minio.tduckcloud.com/landing/bb326f9678cb4874b0a5e53b186ded0d.png',
    description: '数据即时收集解决方案',
    footerList: [
      {
        title: '产品列表',
        linkTextList: [{
          label: '社区开源版',
          link: 'https://gitee.com/TDuckApp'
        },
        {
          label: '经典版',
          link: 'https://doc.tduckcloud.com/buyPro/'
        },
        {
          label: 'TDuckX-Vue3版',
          link: 'https://www.tduckcloud.com/x'
        }, {
          label: 'TReport数据大屏',
          link: 'https://gitee.com/TDuckApp/tduck-report-platform'
        },
        {
          label: 'nium牛码二维码',
          link: 'https://nium.top'
        }
        ]
      },
      {
        title: '关于我们',
        linkTextList: [{
          label: '公益组织支持计划',
          link: 'https://x.tduckcloud.com/s/7wzfTiuG'
        }, {
          label: 'Tduck-Life',
          link: 'https://space.bilibili.com/409825300'
        },
        {
          label: '使用条款',
          link: 'https://doc.tduckcloud.com/blog/userAgreement.html'
        },
        {
          label: '免责声明',
          link: 'https://doc.tduckcloud.com/buyPro/disclaimer.html'
        }
        ]
      },
      {
        title: '联系我们',
        linkTextList: [{
          label: '咨询作者',
          link: 'https://pro.tduckcloud.com/s/eOJT6IEA'
        },
        {
          label: 'mama@tduckapp.com',
          link: '#'
        },
        {
          label: '文档中心',
          link: 'https://doc.tduckcloud.com/'
        }
        ]
      },
      {
        title: '友情链接',
        linkTextList: [
        {
          label: '交换友链',
          link: 'https://x.tduckcloud.com/s/3c1CjVYX'
        }
        ]
      }
    ],
    images: 'https://minio.tduckcloud.com/landing/images/serviceQrCode.png',
    cooperate: '合作伙伴：<a href="https://www.bt.cn/u/fwvsEi" target="_blank"><img src="https://www.bt.cn/developer/images/logo.png" style="width: 60px"> </a>宝塔服务器面板，一键全能部署及管理 ',
    location: 'Copyright © 2019 - 2025 TDuckCloud. 湖南省众达数蔚信息技术有限公司- <a target="_blank" style="color: #1890ff" href="https://beian.miit.gov.cn/#/Integrated/index">湘ICP备19005498号-3 </a>&nbsp; All Rights Reserved.填鸭表单版权所有&nbsp;<img src="https://beian.mps.gov.cn/web/assets/logo01.6189a29f.png" style="width: 10px"><a href="https://beian.mps.gov.cn/#/query/webSearch?code=43058102000053" rel="noreferrer" target="_blank"  style="color: #1890ff">湘公网安备43058102000053号</a>'
  }
}

</script>
<style>
.app-box {

}
</style>
